import React, { useEffect, useRef, useState } from 'react'
import { Badge, Box, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { AutoAwesome } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { assistantActivityLabel } from './assistantActivityLabel'
import useAssistantAvailable from './hooks/useAssistantAvailable'
import useAssistantChat from './hooks/useAssistantChat'
import AssistantChatDrawer from './AssistantChatDrawer'

// The assistant is the only entry point to the chat, so it gets the filled
// treatment instead of the plain grey icons around it in the TopBar.
const buttonStyle = {
  width: '2.5rem',
  height: '2.5rem',
  color: '#212121',
  background: 'linear-gradient(135deg, #7FE1A7, #05fd92)',
  boxShadow: '0px 0px 10px #05fd9260',
  transition: 'transform 150ms ease-out, box-shadow 150ms ease-out',
  '&:hover': {
    background: 'linear-gradient(135deg, #05fd92, #7FE1A7)',
    boxShadow: '0px 0px 16px #05fd92a0',
    transform: 'scale(1.08)'
  }
}

// Remembered per device: a new icon in a crowded bar goes unnoticed, so the
// button breathes until the assistant has been opened once
const DISCOVERED_KEY = 'assistant-discovered'
const readDiscovered = () => window.localStorage.getItem(DISCOVERED_KEY) === 'true'

const discoveryPulseSx = {
  '@keyframes assistantDiscoveryPulse': {
    '0%, 100%': { boxShadow: '0px 0px 10px #05fd9260' },
    '50%': { boxShadow: '0px 0px 22px #05fd92d0, 0px 0px 0px 6px #05fd9230' }
  },
  animation: 'assistantDiscoveryPulse 2.2s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
}

/**
 * Owns the assistant chat: the TopBar button, the Ctrl+K shortcut and the
 * drawer. The conversation state lives here, above the drawer, so the button
 * can show what the assistant is doing while the panel is closed — answers
 * can take up to a minute and nobody should have to keep the panel open to
 * find out whether it is still working.
 *
 * Nothing renders until the gateway answers its health probe: the assistant
 * is not deployed on every MIC.
 */
const AssistantLauncher = () => {
  const { t_ } = useSmartTranslation()
  const { available, recheck } = useAssistantAvailable()
  const chat = useAssistantChat({ onConnectionLost: recheck })
  const [open, setOpen] = useState(false)
  const [unseenAnswer, setUnseenAnswer] = useState(false)
  const [discovered, setDiscovered] = useState(readDiscovered)
  const wasSendingRef = useRef(false)
  const { sending, cancelStreaming } = chat

  // Stop the generation on the gateway if the assistant goes away mid-answer
  useEffect(() => cancelStreaming, [cancelStreaming])

  // A turn that ends with the panel closed leaves a mark on the button
  useEffect(() => {
    if (wasSendingRef.current && !sending && !open) {
      setUnseenAnswer(true)
    }
    wasSendingRef.current = sending
  }, [sending, open])

  useEffect(() => {
    if (open) {
      setUnseenAnswer(false)
      // the first open ends the discovery pulse for good on this device
      window.localStorage.setItem(DISCOVERED_KEY, 'true')
      setDiscovered(true)
    }
  }, [open])

  // Ctrl/Cmd+K opens the assistant from anywhere in the app
  useEffect(() => {
    if (!available) {
      return undefined
    }
    const openOnShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', openOnShortcut)
    return () => document.removeEventListener('keydown', openOnShortcut)
  }, [available])

  if (!available) {
    return null
  }

  const tooltip = sending
    ? assistantActivityLabel(t_, chat)
    : (unseenAnswer ? t_('I have finished') : `${t_('Assistant')} (Ctrl+K)`)

  return (
    <>
      <AssistantChatDrawer chat={chat} open={open} onClose={() => setOpen(false)} />
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {sending
          ? (
            <CircularProgress
              size='3rem'
              thickness={2}
              aria-hidden
              sx={{
                position: 'absolute',
                top: '-0.25rem',
                left: '-0.25rem',
                color: theme => theme.palette.primary.main,
                pointerEvents: 'none'
              }}
            />
            )
          : null}
        <Badge variant='dot' color='info' overlap='circular' invisible={!unseenAnswer}>
          <Tooltip title={tooltip}>
            <IconButton
              onClick={() => setOpen(true)}
              sx={discovered ? buttonStyle : { ...buttonStyle, ...discoveryPulseSx }}
              aria-label={t_('Assistant')}
              aria-busy={sending}
            >
              <AutoAwesome fontSize='small' />
            </IconButton>
          </Tooltip>
        </Badge>
      </Box>
    </>
  )
}

export default AssistantLauncher
